import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { ssoAuthorize, type SsoAuthorizeResponse } from '../api/sso';

/**
 * Login page. Two-step when the user has TOTP 2FA enabled:
 *   1. Email + password → server returns either an access token
 *      (no 2FA) or a TOTP challenge token (2FA on).
 *   2. If a challenge came back, we swap the form into a single
 *      "enter the 6-digit code" field plus a "use a backup code
 *      instead" toggle. Submitting that posts to /login/totp and
 *      finishes the sign-in.
 *
 * On success, a first-ever login goes to /profile for onboarding;
 * every returning user lands on /dashboard. We deliberately do NOT
 * return them to whatever protected page they were on when a session
 * expired — a re-login always starts at the role-aware dashboard, not
 * (say) the Cohort-baseline page they happened to be bounced from.
 */
export function Login() {
  const { login, completeTotp } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // SSO probe result for the entered email's domain (ENTERPRISE-SSO).
  // Populated on email blur; when `sso` is true we surface an
  // "organization sign-in" button, and when `enforce` is true we hide
  // the password fallback entirely.
  const [ssoInfo, setSsoInfo] = useState<SsoAuthorizeResponse | null>(null);

  const handleEmailBlur = async () => {
    const trimmed = email.trim();
    // Only probe once we have something that looks like a full address —
    // avoids a request per keystroke and per half-typed local part.
    if (!trimmed.includes('@') || !trimmed.split('@')[1]?.includes('.')) {
      setSsoInfo(null);
      return;
    }
    try {
      setSsoInfo(await ssoAuthorize(trimmed));
    } catch {
      // Network/other error → silently fall back to password login.
      setSsoInfo(null);
    }
  };

  const startSso = () => {
    if (ssoInfo?.authorize_url) {
      // Full-page navigation out to the IdP — not an in-page axios call.
      window.location.assign(ssoInfo.authorize_url);
    }
  };

  // The SSO callback bounces failures back here as `?sso_error=<reason>`.
  // Surface a friendly message once, then strip the param so a refresh
  // doesn't re-show it.
  useEffect(() => {
    const reason = searchParams.get('sso_error');
    if (!reason) return;
    setError(
      "Single sign-on didn't complete. Please try again, or sign in with " +
        'your password.',
    );
    searchParams.delete('sso_error');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Second-step state. `challenge` being non-null is the signal we
  // flip the form into TOTP mode. `useBackup` toggles the 6-digit
  // field for the dashed backup-code one.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.kind === 'needs_totp') {
        setChallenge(result.challenge_token);
      } else {
        // First-ever login → /profile for onboarding; returning users
        // always land on the role-aware dashboard.
        navigate(result.firstLogin ? '/profile' : '/dashboard', {
          replace: true,
        });
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'sign-in failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTotpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await completeTotp(challenge, {
        code: useBackup ? undefined : code.trim(),
        backupCode: useBackup ? backupCode.trim() : undefined,
      });
      navigate(result.firstLogin ? '/profile' : '/dashboard', {
        replace: true,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'verification failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Stack spacing={3}>
        <Box
          component="img"
          src="/logo.svg"
          alt="10NEWTONS"
          sx={{
            display: 'block',
            mx: 'auto',
            // viewBox is 384×88 — height anchored so the mark scales
            // with whatever the surrounding stack gives it.
            width: 'min(100%, 360px)',
            height: 'auto',
          }}
        />
        <Typography variant="h4">
          {challenge ? 'Two-factor authentication' : 'Sign in'}
        </Typography>

        {challenge === null && (
          <Box component="form" onSubmit={handlePasswordSubmit} autoComplete="on">
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // Invalidate a prior probe as soon as the address
                  // changes — stale SSO state must not gate a new domain.
                  if (ssoInfo) setSsoInfo(null);
                }}
                onBlur={handleEmailBlur}
                autoComplete="email"
                disabled={submitting}
              />
              {ssoInfo?.sso && (
                <>
                  <Button
                    variant="contained"
                    onClick={startSso}
                    disabled={submitting}
                  >
                    Sign in with your organization
                  </Button>
                  {!ssoInfo.enforce && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      or sign in with a password
                    </Typography>
                  )}
                </>
              )}
              {!ssoInfo?.enforce && (
                <>
                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={submitting}
                  />
                  {error && <Alert severity="error">{error}</Alert>}
                  <Button
                    type="submit"
                    variant={ssoInfo?.sso ? 'outlined' : 'contained'}
                    disabled={submitting}
                  >
                    {submitting ? 'Signing in…' : 'Sign in'}
                  </Button>
                </>
              )}
              {ssoInfo?.enforce && error && (
                <Alert severity="error">{error}</Alert>
              )}
            </Stack>
          </Box>
        )}

        {challenge !== null && (
          <Box component="form" onSubmit={handleTotpSubmit}>
            <Stack spacing={2}>
              <Typography color="text.secondary" variant="body2">
                {useBackup
                  ? 'Enter one of the recovery codes saved during enrollment.'
                  : 'Open your authenticator app and enter the current 6-digit code.'}
              </Typography>
              {!useBackup && (
                <TextField
                  label="6-digit code"
                  fullWidth
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputProps={{ inputMode: 'numeric', maxLength: 8 }}
                  autoFocus
                  disabled={submitting}
                />
              )}
              {useBackup && (
                <TextField
                  label="Recovery code"
                  fullWidth
                  required
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value)}
                  placeholder="abcd-ef01-2345"
                  autoFocus
                  disabled={submitting}
                />
              )}
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Verifying…' : 'Verify'}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setUseBackup(!useBackup);
                  setCode('');
                  setBackupCode('');
                  setError(null);
                }}
                disabled={submitting}
              >
                {useBackup
                  ? 'Use the 6-digit code instead'
                  : "Lost access? Use a recovery code"}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Both links live on the same row below the form so the
            two affordances visually balance — earlier the
            "Forgot password?" link sat inside the form Stack while
            "Create an account" sat in the outer Stack, which made
            their vertical rhythm look mismatched. */}
        {challenge === null && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={1}
          >
            <Typography variant="body2" color="text.secondary">
              New here?{' '}
              <MuiLink component={Link} to="/register">
                Create an account
              </MuiLink>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <MuiLink component={Link} to="/forgot-password">
                Forgot password?
              </MuiLink>
            </Typography>
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          <MuiLink component={Link} to="/terms" color="inherit">
            Terms of Use
          </MuiLink>
          {' · '}
          <MuiLink component={Link} to="/privacy" color="inherit">
            Privacy Policy
          </MuiLink>
        </Typography>
      </Stack>
    </Container>
  );
}
